import { Box, Divider, Typography } from '@material-ui/core'
import ResponsiveModal, {
  ResponsiveModalBody,
} from '@src/components/ResponsiveModal/ResponsiveModal'
import {
  OrderItemTrackingInfo,
  ScheduleLineStatusType,
} from '@src/types/graphql-types'
import { OrderDetailsFragment } from '@src/queries/OrderDetailsQuery.generated'
import React, { useEffect, useMemo } from 'react'
import { FormattedDate, FormattedMessage } from 'react-intl'
import { messages } from '../messages'
import OrderDetailItemsTrackingChina from '../OrderDetail/OrderDetailItemsTrackingChina'
import {
  checkIfDropShip,
  getOrderItemsStatusKey,
} from '../OrderDetail/orderDetailUtils'
import LinkToItemShipmentTracking from '@src/routes/OrderCenter/OrderDetail/LinkToItemShipmentTracking'
import { OrderItemNumber } from '../OrderItem/OrderItemNumber'
import { getDateMessage, getStatusMessage } from './config'
import { makeStyles } from '@material-ui/core/styles'
import { OrderItemFragmentWithMergedLines } from './utils'
import { sendOrderCenterEvent } from '@src/routes/OrderCenter/analytics'
import { useCurrentUser } from '@src/utils/useCurrentUser'
import countriesMap from '@localization/countries.json'
import { useCheckUserCountry } from '@src/utils/useCheckUserCountry'

const useStyles = makeStyles((theme) => ({
  divider: {
    margin: theme.spacing(6, 0),
  },
  list: {
    listStyleType: 'none',
    margin: 0,
    padding: 0,
    '& > *': {
      background: theme.palette.common.white,
      borderRadius: 4,
      border: `1px solid ${theme.palette.grey[400]}`,
      padding: theme.spacing(4),
      marginBottom: theme.spacing(4),
      fontSize: theme.typography.pxToRem(12),
    },
  },
  bold: {
    fontWeight: theme.typography.fontWeightMedium,
  },
  detailsList: {
    margin: 0, // override <dl> default
    '& dd': {
      marginLeft: 0, // override <dd> default
    },
    '& > *': {
      marginTop: theme.spacing(4),
    },
  },
  trackingLink: {
    '& > span': {
      fontSize: 'inherit',
    },
  },
}))

interface ShipmentSummaryModalProps {
  order: OrderDetailsFragment
  item: OrderItemFragmentWithMergedLines
  onClose: () => void
  setTrackingInfo: React.Dispatch<
    React.SetStateAction<OrderItemTrackingInfo | null>
  >
}

export const ShipmentSummaryModal: React.FC<ShipmentSummaryModalProps> = ({
  order,
  item,
  onClose,
  setTrackingInfo,
}) => {
  const classes = useStyles()
  const isChinaUser = useCheckUserCountry(countriesMap.CN.countryCode)

  const { userId = '' } = useCurrentUser()
  useEffect(() => {
    sendOrderCenterEvent('view item shipment summary', userId)
  }, [userId])

  const { shippedCount, pendingCount } = useMemo(() => {
    return item.scheduleLines.reduce<{
      shippedCount: number | null
      pendingCount: number | null
    }>(
      (acc, line) => {
        if (acc === null) return acc
        const tableStatusKey = line.status
          ? getOrderItemsStatusKey(line.status)
          : null
        if (tableStatusKey === 'WIMS_ORDER_ITEM_SHIP_STATUS_CANCELLED')
          return acc
        const accKey =
          tableStatusKey === 'SHIPPED_ON' ? 'shippedCount' : 'pendingCount'
        const currentCount = acc[accKey]
        if (currentCount !== null) {
          acc[accKey] = line.confirmedQuantity
            ? line.confirmedQuantity + currentCount
            : null
        }
        return acc
      },
      { shippedCount: 0, pendingCount: 0 }
    )
  }, [item.scheduleLines])

  return (
    <ResponsiveModal
      open
      onClose={onClose}
      renderTitle={() => (
        <Typography variant="h2">
          <FormattedMessage {...messages.SHIPMENT_SUMMARY} />
        </Typography>
      )}
    >
      <ResponsiveModalBody>
        <OrderItemNumber item={item} />
        {shippedCount != null && pendingCount !== 0 && (
          <Box mt={4}>
            {pendingCount != null ? (
              <FormattedMessage
                {...messages.X_OF_Y_SHIPPED}
                values={{
                  x: shippedCount,
                  y: shippedCount + pendingCount,
                }}
              />
            ) : (
              <>
                {shippedCount} <FormattedMessage {...messages.SHIPPED} />
              </>
            )}
          </Box>
        )}
        <Divider className={classes.divider} />
        <ul className={classes.list}>
          {item.scheduleLines.map((line, i) => {
            const isDropShip = line.status && checkIfDropShip(line.status)
            const tableStatusKey = line.status
              ? getOrderItemsStatusKey(line.status)
              : null
            const tableStatusMessage = getStatusMessage(tableStatusKey)
            const dateMessage = getDateMessage(tableStatusKey)
            return (
              <li key={i}>
                <div className={classes.bold}>
                  {!!line.confirmedQuantity && `${line.confirmedQuantity} `}
                  {tableStatusMessage ? (
                    <FormattedMessage {...tableStatusMessage} />
                  ) : (
                    '-'
                  )}
                </div>
                <dl className={classes.detailsList}>
                  <div>
                    <dt>
                      <FormattedMessage {...dateMessage} />:
                    </dt>
                    <dd>
                      {!isDropShip &&
                      line.status !== ScheduleLineStatusType.Delayed &&
                      line.displayableDate ? (
                        <FormattedDate
                          value={new Date(line.displayableDate)}
                          year="numeric"
                          month="short"
                          day="numeric"
                          timeZone="UTC"
                        />
                      ) : (
                        '-'
                      )}
                    </dd>
                  </div>
                  {line.trackingInfo.some(
                    (info) => info.estimatedDeliveryDate
                  ) ? (
                    <div>
                      <dt>
                        <FormattedMessage
                          id="ESTIMATED_DELIVERY_DATE"
                          defaultMessage="Estimated Delivery Date"
                        />
                        :
                      </dt>
                      {line.trackingInfo.map((info) => (
                        <dd key={info.trackingNumber}>
                          {info.estimatedDeliveryDate ? (
                            <FormattedDate
                              value={new Date(info.estimatedDeliveryDate)}
                              year="numeric"
                              month="short"
                              day="numeric"
                              timeZone="UTC"
                            />
                          ) : (
                            '-'
                          )}
                        </dd>
                      ))}
                    </div>
                  ) : (
                    <div>
                      <dt>
                        <FormattedMessage {...messages.CARRIER} />:
                      </dt>
                      <dd>{line.routeInfo || '-'}</dd>
                    </div>
                  )}
                  <div>
                    <dt>
                      <FormattedMessage {...messages.TRACKING} />:
                    </dt>
                    <dd>
                      {!line.trackingInfo.length ? (
                        '-'
                      ) : isChinaUser ? (
                        <OrderDetailItemsTrackingChina
                          line={line}
                          setTrackingInfo={setTrackingInfo}
                          excludeLabel
                          excludeDeliveryId
                        />
                      ) : (
                        line.trackingInfo.map((tracking, index) => (
                          <LinkToItemShipmentTracking
                            tracking={tracking}
                            materialNumber={item.materialNumber}
                            orderNumber={order.orderNumber}
                            shipToZip={order?.shipTo?.postalCode}
                            classes={{ trackingLink: classes.trackingLink }}
                            key={index}
                          />
                        ))
                      )}
                    </dd>
                  </div>
                </dl>
              </li>
            )
          })}
        </ul>
      </ResponsiveModalBody>
    </ResponsiveModal>
  )
}
